<template>
	<div class="content-page content-page--transaction-audit-history">
		<div class="content-page__heading">
			<div class="content-page__breadcrumb-title">
				<h2 class="content-page__title">
					Transaction Audit History
				</h2>
			</div>
			<div class="content-page__actions" ref="contactPageAction">
				<Button
					className="btn--has-icon btn--small"
					:btnClick="exportAll"
					labelText="Export"
					iconClassName="icon-export"
					:iconBeforeLabel="true"
					:disabled="!hasResults"
				/>
			</div>
		</div>

		<div class="results-toolbar results-toolbar--variant-three">
			<TransactionAuditFilters
				v-model="filters"
				:defaultFilters="defaultFilters"
				:userOptions="ddUserOptions || []"
				:cclOptions="ddCclOptions"
				placeholder="Search by user, role, summary or CCL"
				@load-more-users="loadMoreUsers"
				@search-users="searchUsers"
				@load-more-ccl="loadMoreCCL"
				@search-ccl="searchCCL"
				ref="transactionAuditFiltersRef"
			/>
			<div class="results-toolbar__counter" v-if="resultCounter">
				<ResultCounter :counter="resultCounter" />
			</div>
		</div>

		<ScrollContainer
			v-if="hasResults && !loading"
			:class="{ 'is-disabled': exportingAllLogs }"
			data-test="pageContent"
		>
			<Table
				class="table-container--transaction-audit-history"
				:sourceObject="tableSource"
				@change="updatedPagination"
				@row-expanded="requestPayload"
				:isArrowUp="true"
				:collapse="collapse"
				:locked="expandedRowContentLoading"
			>
				<template #col-group>
					<colgroup>
						<!-- Trigger-->
						<col
							v-if="collapse"
							class="table__col table__col--trigger"
						/>

						<!-- Date/Time -->
						<col class="table__col table__col--date" />

						<!-- Changed by -->
						<col class="table__col table__col--changed-by" />

						<!-- Summary -->
						<col class="table__col table__col--summary" />

						<!-- CCL -->
						<col class="table__col table__col--ccl" />

						<!-- Copy/Download -->
						<col class="table__col table__col--action" />
					</colgroup>
				</template>
				<template #body-timestamp="{ body }">
					<div class="transaction-audit-history-data">
						<div class="transaction-audit-history-data__date-time">
							<span
								class="transaction-audit-history-data__text"
								data-test="dateTime"
							>
								{{ shortDateString(body.dateTime) }}
								{{ timeAndTimeZone(body.dateTime) }}
							</span>
						</div>

						<div
							class="transaction-audit-history-data__text-emphasis"
						>
							{{ body.dateDifference }}
						</div>
					</div>
				</template>
				<template #body-summary="{ body }">
					<div class="transaction-audit-history-data">
						<div
							class="transaction-audit-history-data__text-supporting"
							v-if="body.summary.id"
						>
							ID: <strong>{{ body.summary.id }}</strong>
						</div>

						<div
							class="transaction-audit-history-data__text-supporting"
							v-if="body.summary.rawTransactions"
						>
							Raw Transactions:
							<strong>
								{{ body.summary.rawTransactions }}
							</strong>
						</div>
						<div
							class="transaction-audit-history-data__text-supporting"
							v-if="body.summary.transactionRequest"
						>
							Transaction Request:
							<strong>{{
								body.summary.transactionRequest
							}}</strong>
						</div>
						<div
							class="transaction-audit-history-data__text-supporting"
							v-if="body.summary.transactionResponse"
						>
							Transaction Response:
							<strong>{{
								body.summary.transactionResponse
							}}</strong>
						</div>
						<div
							class="transaction-audit-history-data__text-supporting"
							v-if="body.summary.allDownloaded"
						>
							Transaction:
							<strong>{{ body.summary.allDownloaded }}</strong>
						</div>
					</div>
				</template>
				<template #body-ccl="{ body }">
					<span
						class="transaction-audit-history-data__text"
						v-html="body.ccl"
					>
					</span>
				</template>
				<div
					v-for="(item, index) in dataTable"
					:slot="['expand-area' + index]"
					:key="item.transactionId + index"
					class="transaction-audit-history-data__expanded"
				>
					<div
						class="transaction-audit-history-data__expanded-corrupted"
						v-if="
							!auditTransactionsExpanded[index] &&
								!expandedRowContentLoading
						"
					>
						Corrupted Data!
					</div>

					<div
						class="transaction-audit-history-data__expanded-container"
						v-else
					>
						<div
							class="transaction-audit-history-data__expanded-content"
							v-if="auditTransactionsExpanded[index]"
						>
							<div
								class="transaction-audit-history-data__expanded-details"
							>
								<div
									class="transaction-audit-history-data__expanded-details-group"
									v-if="item.transactionId"
								>
									<span
										class="transaction-audit-history-data__text-supporting"
										>Transaction Id:</span
									>
									{{ item.transactionId }}
								</div>
								<div
									class="transaction-audit-history-data__expanded-details-group"
									v-if="item.correlationId"
								>
									<span
										class="transaction-audit-history-data__text-supporting"
										>Correlation Id:</span
									>
									{{ item.correlationId }}
								</div>
								<div
									class="transaction-audit-history-data__expanded-details-group"
									v-if="
										auditTransactionsExpanded[index]
											.logEntryTransactionTypeName
									"
								>
									<span
										class="transaction-audit-history-data__text-supporting"
										>Transaction Type:</span
									>
									{{
										auditTransactionsExpanded[index]
											.logEntryTransactionTypeName
									}}
								</div>
								<div
									class="transaction-audit-history-data__expanded-details-group"
									v-if="item.integratorName"
								>
									<span
										class="transaction-audit-history-data__text-supporting"
										>Vendor:</span
									>
									{{ item.integratorName }}
								</div>
							</div>

							<div
								class="transaction-audit-history-data__expanded-payloads"
								v-if="auditTransactionsExpanded[index].details"
							>
								<div
									class="transaction-audit-history-data__text-emphasis"
								>
									Transaction Payloads
								</div>

								<div
									class="transaction-audit-history-data__expanded-payloads-raw"
								>
									<div
										class="transaction-audit-history-data__expanded-payloads-head"
									>
										<span
											class="transaction-audit-history-data__text"
											>Raw Transactions</span
										>
										<div
											class="transaction-audit-history-data__expanded-payloads-head-tags"
										>
											<TagLabel
												v-if="
													item.rawViewedDateLastAction
												"
												:message="
													'VIEWED: ' +
														timeAndTimeZone(
															item.rawViewedDateLastAction
														)
												"
												:state="'secondary-1'"
											/>
											<TagLabel
												v-if="
													item.rawCopiedDateLastAction
												"
												:message="
													'COPIED: ' +
														timeAndTimeZone(
															item.rawCopiedDateLastAction
														)
												"
												:state="'secondary-2'"
											/>
											<TagLabel
												v-if="
													item.rawDownloadedDateLastAction
												"
												:message="
													'DOWNLOADED: ' +
														timeAndTimeZone(
															item.rawDownloadedDateLastAction
														)
												"
												:state="'secondary-3'"
											/>
										</div>
									</div>

									<pre
										class="transaction-audit-history-data__expanded-payloads-json"
										>{{
											formatToJSON(
												auditTransactionsExpanded[index]
													.details
											)
										}}
									</pre
									>
								</div>
								<div
									class="transaction-audit-history-data__expanded-payloads-request"
									v-if="
										auditTransactionsExpanded[index]
											.requestBlobData
									"
								>
									<div
										class="transaction-audit-history-data__expanded-payloads-head"
									>
										<span
											class="transaction-audit-history-data__text"
											>Transaction Request</span
										>
										<div
											class="transaction-audit-history-data__expanded-payloads-head-tags"
										>
											<TagLabel
												v-if="
													item.requestViewedDateLastAction
												"
												:message="
													'VIEWED: ' +
														timeAndTimeZone(
															item.requestViewedDateLastAction
														)
												"
												:state="'secondary-1'"
											/>
											<TagLabel
												v-if="
													item.requestCopiedDateLastAction
												"
												:message="
													'COPIED: ' +
														timeAndTimeZone(
															item.requestCopiedDateLastAction
														)
												"
												:state="'secondary-2'"
											/>
											<TagLabel
												v-if="
													item.requestDownloadedDateLastAction
												"
												:message="
													'DOWNLOADED: ' +
														timeAndTimeZone(
															item.requestDownloadedDateLastAction
														)
												"
												:state="'secondary-3'"
											/>
										</div>
									</div>

									<pre
										class="transaction-audit-history-data__expanded-payloads-json"
									><code class="language-xml">{{ auditTransactionsExpanded[index].requestBlobData }}</code>
								</pre>
								</div>
								<div
									class="transaction-audit-history-data__expanded-payloads-response"
									v-if="
										auditTransactionsExpanded[index]
											.responseBlobData
									"
								>
									<div
										class="transaction-audit-history-data__expanded-payloads-head"
									>
										<span
											class="transaction-audit-history-data__text"
											>Transaction Response</span
										>
										<div
											class="transaction-audit-history-data__expanded-payloads-head-tags"
										>
											<TagLabel
												v-if="
													item.responseViewedDateLastAction
												"
												:message="
													'VIEWED: ' +
														timeAndTimeZone(
															item.responseViewedDateLastAction
														)
												"
												:state="'secondary-1'"
											/>
											<TagLabel
												v-if="
													item.responseCopiedDateLastAction
												"
												:message="
													'COPIED: ' +
														timeAndTimeZone(
															item.responseCopiedDateLastAction
														)
												"
												:state="'secondary-2'"
											/>
											<TagLabel
												v-if="
													item.responseDownloadedDateLastAction
												"
												:message="
													'DOWNLOADED: ' +
														timeAndTimeZone(
															item.responseDownloadedDateLastAction
														)
												"
												:state="'secondary-3'"
											/>
										</div>
									</div>

									<pre
										class="transaction-audit-history-data__expanded-payloads-json"
									><code class="language-xml">{{auditTransactionsExpanded[index].responseBlobData }}
									</code>
								</pre>
								</div>
							</div>
						</div>

						<div
							v-if="
								expandedRowContentLoading &&
									latestRowExpandedIndex === index
							"
							class="spinner-row"
						>
							<Spinner :processor="{ loading: true }" />
						</div>
					</div>
				</div>
				<template #body-actions="{ body }">
					<div class="transaction-audit-history-data__action">
						<Button
							className="btn--icon-only"
							:btnClick="
								() => {
									copyRow(body);
								}
							"
							:iconClassName="'icon icon-copy'"
							:iconBeforeLabel="true"
						/>
						<Button
							className="btn--icon-only"
							:btnClick="
								() => {
									downloadFile(body);
								}
							"
							:iconClassName="'icon icon-download'"
							:iconBeforeLabel="true"
						/>
					</div>
				</template>
			</Table>
		</ScrollContainer>
		<Spinner
			v-if="loading || exportingAllLogs"
			:processor="{ loading: true }"
			data-test="spinnerPageContent"
		/>
		<NoResults v-if="!hasResults && !loading" data-test="noResults" />
	</div>
</template>

<script>
import ScrollContainer from '@/components/shells/scroll-container/scroll-container';
import NoResults from '@/site-components/no-results/no-results';
import ResultCounter from '@/site-components/result-counter/result-counter';
import Spinner from '@/components/visual/spinner/spinner';
import Table from '@/components/shells/table/table';
import CustomerOptionsMixins from '@/mixins/customers/customer-options';
//;
import DatetimeMixin from '@/mixins/datetime-mixin';
import TransactionAuditFilters from '@/sections/transactions/audit/transaction-audit-filters.vue';
import Button from '@/components/forms/button/button';
import TagLabel from '@/components/visual/tag-label';
//const
import headerOptions from './header-options';

// libraries
import _ from 'lodash';
import { sub } from 'date-fns';
import BlobService from '@/services/blob-service';

// utils
import { formatDate } from '@/utils/datetime';
import {
	removeObjectEmptyValues,
	removePropertiesWithSameValues,
	downloadCSV
} from '@/utils/object';
import { copyToClipboard } from '@/utils/function';

// vuex
import { mapActions, mapState } from 'vuex';
import store from '@/store';

export default {
	name: 'GlobalTransactionAudit',
	mixins: [ CustomerOptionsMixins, DatetimeMixin],
	components: {
		ScrollContainer,
		ResultCounter,
		NoResults,
		Spinner,
		Table,
		TransactionAuditFilters,
		Button,
		TagLabel
	},
	props: {
		search: String,
		minDatetime: String,
		maxDatetime: String,
		userId: String,
		ccl: String,
		page: Number,
		countPerPage: Number
	},
	data() {
		return {
			filters: {},
			defaultFilters: {
				orderBy: 'timestamp',
				orderDirection: 'desc',
				search: undefined,
				minDatetime: formatDate(
					new Date(sub(new Date(), { weeks: 1 }))
				),
				maxDatetime: formatDate(new Date()),
				userId: undefined,
				ccl: undefined,
				page: 1,
				countPerPage: 10
			},
			tableSource: {
				headers: [],
				displayHeader: true,
				source: {}
			},
			loading: true,
			exportingAllLogs: false,
			expandedRowContentLoading: false,
			latestRowExpandedIndex: -1,
			userPage: 1,
			userCount: 100,
			userSearch: undefined,
			ddUserOptions: [],
			cclPage: 1,
			cclCount: 100,
			cclSearch: undefined,
			transactionsColumns: {
				datetime: 'datetime',
				userAndRole: 'userFullnameRoleName',
				summary: 'details',
				ccl: 'ccl'
			},
			collapse: true,
			auditTransactionsExpanded: []
		};
	},
	async created() {
		this.tableSource.headers = headerOptions;

		if (_.get(this.userOptions, 'items', []).length > 0) {
			this.ddUserOptions = this.userOptions.items;
		} else {
			await this.fetchUsers();
		}
	},
	computed: {
		...mapState('globaltransactions', [
			'transactionAuditLog',
			'allTransactionAuditLog'
		]),
		...mapState('users', ['users']),
		...mapState('options', ['userOptions', 'cclOptions']),

		resultCounter() {
			return this.tableSource.source.totalCount;
		},
		hasResults() {
			return (
				this.tableSource.source.totalCount !== undefined &&
				this.tableSource.source.totalCount > 0
			);
		},
		hasMoreUsersToLoad() {
			return this.userPage < (this.userOptions.totalPageCount || 0);
		},
		csvDataFileName() {
			let today = new Date();
			let strDay = today.toLocaleString();

			return `transaction_log_${strDay.split(', ')[0]}_${
				strDay.split(', ')[1]
			}.csv`;
		},
		hasMoreCCLToLoad() {
			return this.cclPage < (this.cclOptions.totalPageCount || 0);
		},
		dataTable() {
			return this.tableSource.source.items || [];
		},
		dateRange() {
			return {
				minDatetime: this.minDatetime,
				maxDatetime: this.maxDatetime
			};
		},
		ddCclOptions() {
			return _.get(this.cclOptions, 'items', []);
		}
	},
	methods: {
		...mapActions('globaltransactions', [
			'setTransactionAuditLog',
			'getAllTransactionAuditLog',
			'getGlobalTransactionById'
		]),
		...mapActions('users', ['setUsers']),
		...mapActions('options', ['getUsersOptions', 'getCCLOptions']),
		...mapActions('download', ['setDownloadAll']),
		isValidJson(json) {
			try {
				JSON.parse(json);
				return true;
			} catch (e) {
				return false;
			}
		},
		formatToJSON(value) {
			if (!_.isEmpty(value) && this.isValidJson(value)) {
				return JSON.stringify(JSON.parse(value), null, 2);
			}
			return '';
		},
		async fetchDataLogs() {
			this.loading = true;
			let query = removeObjectEmptyValues(this.filters);

			const filteredQuery = _.cloneDeep(query);

			filteredQuery.recordStart = this.filters.page;
			filteredQuery.maxCount = this.filters.countPerPage;

			delete filteredQuery.page;
			delete filteredQuery.countPerPage;

			try {
				await this.setTransactionAuditLog(filteredQuery);
				for (
					let i = 0;
					i < this.transactionAuditLog.items.length;
					i++
				) {
					this.auditTransactionsExpanded.push(null);
				}
			} finally {
				this.formatLogs(this.transactionAuditLog);
				this.loading = false;
			}
		},
		async exportAll() {
			this.exportingAllLogs = true;

			const filteredQuery = _.cloneDeep(this.filters);

			filteredQuery.recordStart = this.filters.page;
			filteredQuery.maxCount = this.filters.countPerPage || 500000;

			delete filteredQuery.page;
			delete filteredQuery.countPerPage;

			try {
				await this.getAllTransactionAuditLog(filteredQuery);
			} finally {
				if (!_.isEmpty(this.allTransactionAuditLog)) {
					const formatedLogs = this.formatLogs(
						this.allTransactionAuditLog,
						false
					).items;
					// stringify summary
					formatedLogs.map(log => {
						log['Date/Time'] = log.dateTime;
						log['Date Difference'] = log.dateDifference;
						log['User/Role'] = log.userFullnameRoleName;
						log.Summary = JSON.stringify(log.summary);
						log.CCL = log.ccl;
					});

					const csvOptions = {
						fields: [
							'Date/Time',
							'Date Difference',
							'User/Role',
							'Summary',
							'CCL'
						],
						delimiter: ',', // delimiter CSV (coma is defecto)
						withBOM: true // BOM on true allow to add special characters
					};
					downloadCSV(formatedLogs, csvOptions, 'auditLogs');
				}
				this.exportingAllLogs = false;
			}
		},
		async fetchCCL() {
			return new Promise((resolve, reject) => {
				this.getCCLOptions({
					page: this.cclPage,
					count: this.cclCount,
					sortdirection: 'asc',
					sortby: 'name',
					search: this.cclSearch,
					minDatetime: this.minDatetime,
					maxDatetime: this.maxDatetime
				})
					.then(() => {
						this.$nextTick(() => {
							if (
								this.$refs.transactionAuditFiltersRef?.$refs
									.cclDropdownRef
							) {
								this.$refs.transactionAuditFiltersRef?.$refs.cclDropdownRef.stopLoading();
							}

							if (
								this.$refs.transactionAuditFiltersRef?.$refs
									.cclDropdownModalRef
							) {
								this.$refs.transactionAuditFiltersRef?.$refs.cclDropdownModalRef.stopLoading();
							}
						});
						resolve();
					})
					.catch(err => {
						reject(err);
					});
			});
		},
		async fetchUsers() {
			return new Promise((resolve, reject) => {
				this.getUsersOptions({
					page: this.userPage,
					count: this.userCount,
					sortdirection: 'asc',
					sortby: 'name',
					search: this.userSearch
				})
					.then(() => {
						this.ddUserOptions = this.userOptions.items;

						this.$nextTick(() => {
							if (
								this.$refs.transactionAuditFiltersRef?.$refs
									.usersDropdownRef
							) {
								this.$refs.transactionAuditFiltersRef?.$refs.usersDropdownRef.stopLoading();
							}

							if (
								this.$refs.transactionAuditFiltersRef?.$refs
									.usersDropdownModalRef
							) {
								this.$refs.transactionAuditFiltersRef?.$refs.usersDropdownModalRef.stopLoading();
							}
						});
						resolve();
					})
					.catch(err => {
						reject(err);
					});
			});
		},
		async loadMoreUsers() {
			this.userPage += 1;

			await this.fetchUsers();
		},
		async loadMoreCCL() {
			this.cclPage += 1;

			await this.fetchCCL();
		},
		async searchUsers(searchQuery) {
			this.userPage = 1;
			this.userSearch = searchQuery;

			await this.fetchUsers();
		},
		async searchCCL(searchQuery) {
			this.cclPage = 1;
			this.cclSearch = searchQuery;

			await this.fetchCCL();
		},
		async downloadRawReqRes(transactionId) {
			await this.setDownloadAll({
				logId: transactionId,
				level: 'INFO'
			}).then(data => {
				if (data) {
					BlobService.DownloadAll(data, 'blob', transactionId);
				}
			});
		},
		downloadBlobFile(uri) {
			return BlobService.DownloadSecurevueTransactionContent(
				uri,
				'application/xml'
			);
		},
		copyRow(body) {
			const row = `Date/Time: ${body.dateTime} ${body.dateDifference}\n
User/Role: ${body.userFullnameRoleName}\n
Summary: ${JSON.stringify(body.summary)}\n
CCL: ${body.ccl}`;
			copyToClipboard(row);

			return store.dispatch('toastMessages/push', {
				statusCode: 200,
				message: 'Row copied to clipboard!!'
			});
		},
		async downloadFile(body) {
			const auditTransaction = await this.getGlobalTransactionById({
				transactionId: body.transactionId
			});

			const details = JSON.parse(auditTransaction.items[0].details);

			if (details.RequestBlob && details.ResponseBlob) {
				this.downloadRawReqRes(body.transactionId);
			} else {
				this.updateToast({
					status: 100,
					message:
						'Transaction has not request and response to download...'
				});
			}
		},
		async requestPayload(rowExpandedIndex) {
			this.latestRowExpandedIndex = rowExpandedIndex;

			if (!this.auditTransactionsExpanded[rowExpandedIndex]) {
				const transactionItem = this.transactionAuditLog.items[
					rowExpandedIndex
				];
				try {
					this.expandedRowContentLoading = true;
					const auditTransaction = await this.getGlobalTransactionById(
						{ transactionId: transactionItem.transactionId }
					);

					this.auditTransactionsExpanded[rowExpandedIndex] =
						auditTransaction.items[0];

					if (!auditTransaction.items[0]) {
						return;
					}
					const details = JSON.parse(
						auditTransaction.items[0].details
					);

					const handleBlob = async (blobUri, property) => {
						if (blobUri) {
							try {
								const blobData = await this.downloadBlobFile(
									blobUri
								);
								this.auditTransactionsExpanded[
									rowExpandedIndex
								][property] = blobData;
							} catch (error) {
								console.log(error);
							}
						}
					};

					await handleBlob(
						details.RequestBlob?.Uri,
						'requestBlobData'
					);
					await handleBlob(
						details.ResponseBlob?.Uri,
						'responseBlobData'
					);
				} finally {
					this.expandedRowContentLoading = false;
				}
			} else {
				this.expandedRowContentLoading = false;
			}
		},
		formatLogs(logs, updateTable = true) {
			let results = _.cloneDeep(logs);
			if (results.items.length > 0) {
				results.items.forEach(item => {
					const rawTransactionsText = [];
					if (item.rawViewedCount) {
						rawTransactionsText.push('Viewed');
					}

					if (item.rawCopiedCount) {
						rawTransactionsText.push('Copied');
					}

					if (item.rawDownloadedCount) {
						rawTransactionsText.push('Downloaded');
					}

					const transactionRequestText = [];
					if (item.requestViewedCount) {
						transactionRequestText.push('Viewed');
					}

					if (item.requestCopiedCount) {
						transactionRequestText.push('Copied');
					}

					if (item.requestDownloadedCount) {
						transactionRequestText.push('Downloaded');
					}

					const transactionResponseText = [];
					if (item.responseViewedCount) {
						transactionResponseText.push('Viewed');
					}

					if (item.responseCopiedCount) {
						transactionResponseText.push('Copied');
					}

					if (item.responseDownloadedCount) {
						transactionResponseText.push('Downloaded');
					}

					item.summary = {
						id: item.transactionId,
						rawTransactions: rawTransactionsText.join(', '),
						transactionRequest: transactionRequestText.join(', '),
						transactionResponse: transactionResponseText.join(', ')
					};
					if (item.allDownloadedCount > 0) {
						Object.assign(item.summary, {
							allDownloaded: 'All Downloaded'
						});
					}

					item.dateDifference = this.differenceDateString(
						item.dateTime
					);
					const dealershipGroupName =
						item && item.dealershipGroupName
							? item.dealershipGroupName
							: null;
					const companyName =
						item && item.companyName ? item.companyName : null;

					if (dealershipGroupName || companyName) {
						let listToRender = [];
						dealershipGroupName &&
							listToRender.push(dealershipGroupName);
						companyName && listToRender.push(companyName);
						item.ccl = listToRender.join('<strong> | </strong>');
					}
				});
			}

			if (updateTable) {
				this.tableSource.source = { ...results };
			}

			return results;
		},
		updateRoute(filters) {
			let queries = removePropertiesWithSameValues(
				filters,
				this.defaultFilters
			);

			queries = removeObjectEmptyValues(queries);

			let pathQuery = ['/transactions', 'audit'];
			if (this.changedEntityId) {
				pathQuery.splice(1, 0, this.changedEntityId);
			}
			const path = pathQuery.join('/');

			this.$router
				.push({
					path: path,
					query: queries
				})
				.catch(() => {});
		},
		updatedPagination(value) {
			this.filters.page = value.page;
			this.filters.countPerPage = value.countPerPage;
		},
		resetFilters() {
			this.filters = this.defaultFilters;
		},
		updateToast(val) {
			this.$store.dispatch('toastMessages/show', {
				toastId: 'transactionAudit.action',
				statusCode: val.status,
				message: val.message,
				slot: 'default',
				useIcon: true,
				userDismissable: true
			});
		}
	},
	watch: {
		filters: {
			deep: true,
			async handler(newquery) {
				this.updateRoute(newquery);
			}
		},
		'$route.query': {
			immediate: true,
			handler(newQuery) {
				if (newQuery.maxCount) {
					const availablemaxCount = [10, 20, 30, 50, 100];

					if (availablemaxCount.includes(this.maxCount)) {
						newQuery.maxCount = this.maxCount;
					} else {
						newQuery.maxCount = this.defaultFilters.maxCount;
					}
				}

				this.filters = { ...this.defaultFilters, ...newQuery };
				this.fetchDataLogs();
			}
		},
		dateRange: {
			deep: true,
			immediate: true,
			handler() {
				if (!this.filters.ccl) {
					this.fetchCCL();
				}
			}
		}
	}
};
</script>

<style lang="scss" scoped>
.spinner-row::v-deep .spinner {
	position: initial;
}
</style>

<style lang="scss">
/**
  * Transaction Audit History
  * Can use SCSS variables = TRUE
**/

.results-toolbar {
	width: 100%;
	margin-bottom: 0;

	&__counter {
		margin-bottom: $gutter-md;
	}
}

/*------------------------------------*\
  Transaction Audit Table
\*------------------------------------*/

.table-container--transaction-audit-history {
	.table {
		@include mq($from: md) {
			table-layout: fixed;
		}
	}

	.table__data {
		word-break: break-word;
	}

	.table__col {
		width: 100%;

		@include mq($from: md) {
			&--trigger {
				width: 32px;
			}
			&--date {
				width: 140px;
			}
			&--changed-by {
				width: 180px;
			}
			&--summary {
				width: auto;
			}
			&--ccl {
				width: 140px;
			}
			&--action {
				width: 120px;
			}
		}

		@include mq($from: lg) {
			&--trigger {
				width: 48px;
			}
			&--date {
				width: 200px;
			}
			&--changed-by {
				width: 280px;
			}

			&--ccl {
				width: 200px;
			}
			&--action {
				width: 120px;
			}
		}
	}
}

/*------------------------------------*\
  Transaction Audit Table Data
\*------------------------------------*/

.transaction-audit-history-data {
	display: flex;
	flex-direction: column;
	gap: $gutter-sm;

	&__date-time {
		display: flex;
		flex-wrap: wrap;
		column-gap: $gutter-lg;
		row-gap: $gutter-sm;
		justify-content: space-between;
	}

	&__text-emphasis,
	strong {
		@include body-text('regular-emphasis');
	}

	&__text-supporting {
		color: $neutral-2;
	}

	&__expanded-corrupted {
		text-align: center;
		padding: $padding-sm 0;
	}

	&__expanded-details {
		display: grid;
		width: max-content;
		grid-auto-flow: column;
		grid-template-columns: auto auto;
		grid-template-rows: repeat(2, 1fr);
		column-gap: $gutter-xxl;
		row-gap: $gutter-md;
		margin-top: $gutter-xl;

		@include mq($until: md-l) {
			grid-auto-flow: initial;
			grid-template-columns: auto;
		}
	}

	&__expanded-payloads {
		margin-top: $gutter-xl;
	}

	&__expanded-payloads-raw {
		margin-top: $gutter-lg;
	}

	&__expanded-payloads-head {
		display: flex;
		justify-content: space-between;
		align-items: center;
	}

	&__expanded-payloads-head-tags {
		display: flex;
		gap: $gutter-md;
	}

	&__expanded-payloads-json {
		background: $white;
		border: 1px solid $neutral-2;
		max-height: 160px;
		overflow-x: hidden;
		overflow-y: auto;
		@include scrollbar;
		font-family: $font-family;
		line-height: 18px;
		padding: $padding-sm;
		margin: $gutter-md 0 $gutter-xl 0;
	}

	&__action {
		display: flex;
		flex-direction: row;
		justify-content: space-around;

		.btn--icon-only {
			font-size: 20px;
		}
	}
}
</style>
